/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { QuickMenuShortcut } from 'client-code/homeshortcut';

import { QuickMenuShortcutServiceRepositoryImpl } from '../impls/QuickMenuShortcutServiceRepositoryImpl';
import { QuickMenuShortcutServiceRepositoryImplV3 } from '../impls/QuickMenuShortcutServiceRepositoryImplV3';
import { IQuickMenuShortcutService } from '../interfaces/IQuickMenuShortcutService';
import { IQuickMenuShortcutServiceRepository } from '../interfaces/IQuickMenuShortcutServiceRepository';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { QuickMenuShortcutServiceV3Contract } from '../servicecontracts';
import { Storage } from '../storage';
import { TServicesParams } from '../types';
import { CcbSwitch } from '../utils/ccb';
import { CcbSwitchName } from '../utils/ccb/enums';

export class QuickMenuShortcutService implements IQuickMenuShortcutService {
    private static TAG = 'QuickMenuShortcutServiceImpl';

    private oQuickMenuShortcutServiceRepository: IQuickMenuShortcutServiceRepository;

    // eslint-disable-next-line max-len
    private oQuickMenuShortcutServiceRepositoryV3: IQuickMenuShortcutServiceRepository<QuickMenuShortcutServiceV3Contract>;

    private mDefaultQuickMenuShortcuts: Array<QuickMenuShortcut> = [
        {
            quickMenuShortcutId: 'tivo:qmsc.4189',
            shortcutKey: 'num4',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_GUIDE_OPTION_TEXT),
            uiAction: {
                destination: 'guide',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
        {
            quickMenuShortcutId: 'tivo:qmsc.4187',
            shortcutKey: 'num2',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_MY_SHOWS_OPTION_TEXT),
            uiAction: {
                destination: 'myShows',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
        {
            quickMenuShortcutId: 'tivo:qmsc.490',
            shortcutKey: 'num6',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_ON_DEMAND_OPTION_TEXT),
            uiAction: {
                destination: 'vodBrowse',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
        {
            quickMenuShortcutId: 'tivo:qmsc.4190',
            shortcutKey: 'num5',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_SETTINGS_OPTION_TEXT),
            uiAction: {
                destination: 'settings',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
    ];

    public constructor(params: TServicesParams) {
        this.oQuickMenuShortcutServiceRepository = new QuickMenuShortcutServiceRepositoryImpl(
            params,
        );
        this.oQuickMenuShortcutServiceRepositoryV3 = new QuickMenuShortcutServiceRepositoryImplV3(
            params,
        );
    }

    /**
     * @inheritdoc
     */
    public async getQuickMenuShortcuts(): Promise<QuickMenuShortcut[]> {
        try {
            const cachedQuickMenuShortcuts = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItem(Storage.Keys.QUICK_MENU_SHORTCUT);
            if (cachedQuickMenuShortcuts) return cachedQuickMenuShortcuts;

            const isV3Enabled = CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_V3_QUICK_MENU_SHORTCUT_REMOVE_IN_KST_16510,
            );

            const quickMenuShortcutResponse = isV3Enabled
                ? // eslint-disable-next-line max-len
                  ((await this.oQuickMenuShortcutServiceRepositoryV3.fetchQuickMenuShortcuts()) as unknown as QuickMenuShortcut[])
                : await this.oQuickMenuShortcutServiceRepository.fetchQuickMenuShortcuts();

            if (!quickMenuShortcutResponse.length)
                throw new Error('Got a response without a "critical" shortcut');

            // store the response in session
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.QUICK_MENU_SHORTCUT,
                quickMenuShortcutResponse,
            );

            return quickMenuShortcutResponse;
        } catch (error: any) {
            Log.e(QuickMenuShortcutService.TAG, 'getQuickMenuShortcuts', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public getDefaultQuickMenuShortcuts(): Array<QuickMenuShortcut> {
        const isV3Enabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_V3_QUICK_MENU_SHORTCUT_REMOVE_IN_KST_16510,
        );
        const isOnDemandEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_BROWSE_MAIN_SCREEN_REMOVE_IN_KST_12159,
        );
        return this.mDefaultQuickMenuShortcuts.filter((item) => {
            if (
                item?.title ===
                Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_ON_DEMAND_OPTION_TEXT)
            ) {
                // When V3 is enabled, VOD is controlled by the V3 API response, not defaults.
                return !isV3Enabled && isOnDemandEnabled;
            }
            return true;
        });
    }

    public getCachedQuickMenuShortcuts(): Array<QuickMenuShortcut> {
        const cachedQuickMenuShortcuts: Array<QuickMenuShortcut> = Storage.getInstance(
            Storage.StorageType.SESSION_STORAGE,
        ).getItem(Storage.Keys.QUICK_MENU_SHORTCUT);
        if (cachedQuickMenuShortcuts) return cachedQuickMenuShortcuts;
        return this.getDefaultQuickMenuShortcuts();
    }

    /**
     * @inheritdoc
     */
    public async fetchQuickMenuShortcutsWithFallback(): Promise<QuickMenuShortcut[]> {
        try {
            return await this.getQuickMenuShortcuts();
        } catch {
            // getQuickMenuShortcuts already logs the error before rethrowing.
            // Fall back to whatever is cached, or defaults if cache is empty.
            return this.getCachedQuickMenuShortcuts();
        }
    }
}
